/* ============================================================
 * custom-gift-guard.js
 *
 * カート特典（エコバッグ）の付与状態をカートページ読み込み時に検証し、
 * 条件と食い違っていれば是正する。
 *
 * 目的:
 *   - 条件未達なのに特典が入っている  → 削除
 *   - 条件達成なのに特典が入っていない → 追加
 *   - 特典が2点以上入っている          → 1点に是正
 *
 * 設計方針:
 *   全ページで動作し、次の3つの契機で判定する。
 *     1) ページ読み込み時（サーバーが埋め込んだ初期状態で判定。通信なし）
 *     2) カートページでのカート変更（DOM の変化を監視）
 *     3) その他のページでのカート変更（テーマの cart:updated イベント）
 *   1 があるため、前セッションのカートやイベントが発火しない経路でも
 *   必ず1回は検証される。2・3 により、商品ページで閾値に達した時点で
 *   特典が入り、カートページには最初から特典がある状態で到達する。
 *
 * 速度:
 *   - <head> で async 読み込みし、DOM の完成を待たずに判定する。
 *     判定材料は layout/theme.liquid が埋め込む初期状態なので通信は不要。
 *   - 是正が必要な場合はリクエストを即座に発行し、ネットワーク往復を
 *     ページの描画と並行させる。描画完了時には応答が揃っていることが多い。
 *   - 是正後はページ全体を再読み込みせず、テーマ本体の refreshCart() と
 *     同じ方法でカート部分だけを差し替える。
 *
 * 設定は window.TAION_GIFT_GUARD（layout/theme.liquid で出力）から受け取る。
 * ============================================================ */
(function () {
  'use strict';

  var CFG = window.TAION_GIFT_GUARD;
  if (!CFG) return;
  if (CFG.mode !== 'log' && CFG.mode !== 'on') return;

  var DRY_RUN = CFG.mode === 'log';
  var TAG = '[gift-guard]';

  /* 除外SKU接頭辞。カンマ区切り文字列を配列に正規化する */
  var EXCLUDE_PREFIXES = String(CFG.excludePrefixesRaw || '')
    .split(',')
    .map(function (s) {
      return s.trim();
    })
    .filter(function (s) {
      return s.length > 0;
    });

  /* 是正の「往復」だけを止めるためのカウンタ。
     他アプリ（EG Auto Add to Cart）と判定が食い違うと
     「当方が削除 → アプリが再追加」の往復が起こり得るため上限を設ける。

     ⚠️ 止めてよいのは付与（add）だけ。条件を満たさないのに特典が残り続ける方が
     問題なので、取り上げる側（remove / normalize）は決して止めない。
     以前は方向を問わず2回で停止していたため、閾値を何度もまたぐ操作で上限に達し、
     「10,000円未満になってもエコバッグが消えない」状態になっていた。

     往復の判定は「直前と逆向きの是正が短時間に連続したか」で行う。
     買い足し／削除で閾値を行き来する通常の操作は間隔が空くため該当しない。 */
  var COUNTER_KEY = 'taion_gift_guard_flips';
  var LAST_ACTION_KEY = 'taion_gift_guard_last_action';
  var LAST_ACTION_AT_KEY = 'taion_gift_guard_last_action_at';
  var MAX_FLIPS = 4;
  var FLIP_WINDOW_MS = 3000;

  function log() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(TAG);
    console.log.apply(console, args);
  }

  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(TAG);
    console.warn.apply(console, args);
  }

  /* Cart API の金額（100倍整数）を円表記にする */
  function yen(cents) {
    return '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
  }

  function getFlipCount() {
    return parseInt(sessionStorage.getItem(COUNTER_KEY) || '0', 10);
  }

  /* 実行した是正を記録する。直前と逆向きの是正が FLIP_WINDOW_MS 以内に
     起きた場合だけ「往復」として数える */
  function noteCorrection(action) {
    var last = sessionStorage.getItem(LAST_ACTION_KEY);
    var lastAt = parseInt(sessionStorage.getItem(LAST_ACTION_AT_KEY) || '0', 10);
    var soon = Date.now() - lastAt < FLIP_WINDOW_MS;
    var reversed =
      (last === 'add' && action === 'remove') || (last === 'remove' && action === 'add');

    sessionStorage.setItem(COUNTER_KEY, String(reversed && soon ? getFlipCount() + 1 : 0));
    sessionStorage.setItem(LAST_ACTION_KEY, action);
    sessionStorage.setItem(LAST_ACTION_AT_KEY, String(Date.now()));
  }

  function resetCorrectionCount() {
    sessionStorage.removeItem(COUNTER_KEY);
    sessionStorage.removeItem(LAST_ACTION_KEY);
    sessionStorage.removeItem(LAST_ACTION_AT_KEY);
  }

  /* 付与（add）のみ、往復が続いているときに見送る。
     取り上げる是正は常に実行する */
  function shouldSkipCorrection(action) {
    return action === 'add' && getFlipCount() >= MAX_FLIPS;
  }

  /* 判定中の多重起動を防ぐ */
  var running = false;

  /* ---------- チェックアウト導線の一時封鎖 ----------
     判定・是正が終わる前にチェックアウトへ進まれると、特典が付かないまま
     注文が確定してしまう。確定するまでレジへの導線を止める。
     万一処理が失敗しても購入を妨げないよう、必ず解除するタイマーを併用する。 */

  var BUSY_FAILSAFE_MS = 5000;
  var BUSY_LABEL = CFG.busyLabel || '特典を確認しています…';
  var GIFT_ONLY_LABEL = CFG.giftOnlyLabel || '特典のみではご購入いただけません';
  var MUTATION_DEBOUNCE_MS = Math.max(200, CFG.settleDelayMs || 0);
  var busyFailsafeTimer = null;

  /* 封鎖の理由は2つあり、別々にボタンを操作すると互いの復元処理で
     打ち消し合う。状態を持って1か所で解決する
       busy          … 判定・是正の最中（数秒で必ず解除される一時的なもの）
       giftOnlyLocked… カートに特典しか入っていない（注文として成立しない） */
  var busy = false;
  var giftOnlyLocked = false;

  function applyCheckoutState() {
    var blocked = busy || giftOnlyLocked;
    var label = giftOnlyLocked ? GIFT_ONLY_LABEL : BUSY_LABEL;
    /* ミニカート内の「購入手続きへ進む」も封鎖対象になるよう、最初の1個ではなく全ボタンを対象にする */
    var btns = document.querySelectorAll('button[name="checkout"]');
    var express = document.querySelector('.additional-checkout-buttons');

    btns.forEach(function (btn) {
      btn.disabled = blocked;
      if (blocked) {
        if (!btn.hasAttribute('data-gift-guard-label')) {
          btn.setAttribute('data-gift-guard-label', btn.textContent);
        }
        btn.textContent = label;
      } else if (btn.hasAttribute('data-gift-guard-label')) {
        btn.textContent = btn.getAttribute('data-gift-guard-label');
        btn.removeAttribute('data-gift-guard-label');
      }
    });

    if (express) {
      express.style.pointerEvents = blocked ? 'none' : '';
      express.style.opacity = blocked ? '0.4' : '';
    }
  }

  function setBusy(isBusy) {
    busy = isBusy;
    applyCheckoutState();

    window.clearTimeout(busyFailsafeTimer);
    if (isBusy) {
      busyFailsafeTimer = window.setTimeout(function () {
        warn('判定が既定時間内に完了しなかったため、チェックアウトの封鎖を解除しました');
        setBusy(false);
      }, BUSY_FAILSAFE_MS);
    }
  }

  /* 特典しか入っていないカートは注文として成立しないため決済導線を止める。
     通常は remove の是正で特典が外れてこの状態にならない。是正が失敗したとき
     のための最終防壁で、商品が入れば次の判定で自動的に解除される */
  function isGiftOnlyCart(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    return cart.items.every(isGift);
  }

  function syncGiftOnlyLock(cart) {
    var locked = isGiftOnlyCart(cart);
    if (giftOnlyLocked === locked) return;
    giftOnlyLocked = locked;
    if (locked) warn('特典のみのカートのため、決済導線を停止しました');
    else log('決済導線の停止を解除しました');
    applyCheckoutState();
  }

  /* Enter キー等でのフォーム送信も塞ぐ */
  function blockSubmitWhileBusy() {
    var form = document.querySelector('.cart__form');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      if (running) {
        e.preventDefault();
        log('判定中のため送信を保留しました');
      }
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error(url + ' -> ' + res.status);
      return res.json();
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  /* ---------- 判定 ---------- */

  function isGift(item) {
    if (CFG.giftVariantId && String(item.variant_id) === String(CFG.giftVariantId)) return true;
    if (CFG.giftSku && item.sku === CFG.giftSku) return true;
    return false;
  }

  /* 判定額から除外する明細。
     - 特典そのもの（現在0円だが、将来価格を付けた場合の自己増幅を防ぐ）
     - 代引き手数料（「お買い上げ額」ではないため）
     ギフトボックス等の有料オプションは判定額に含める（2026-08-17 TAION社合意）。 */
  function isExcluded(item) {
    if (isGift(item)) return true;
    var sku = item.sku || '';
    for (var i = 0; i < EXCLUDE_PREFIXES.length; i++) {
      if (sku.indexOf(EXCLUDE_PREFIXES[i]) === 0) return true;
    }
    return false;
  }

  function eligibleTotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isExcluded(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  function giftLines(cart) {
    return cart.items.filter(isGift);
  }

  function decide(cart) {
    var total = eligibleTotal(cart);
    var lines = giftLines(cart);
    var qty = lines.reduce(function (n, item) {
      return n + item.quantity;
    }, 0);
    var qualifies = total >= CFG.thresholdCents;

    var action = 'none';
    if (qualifies && qty === 0) action = 'add';
    else if (!qualifies && qty > 0) action = 'remove';
    else if (qualifies && (qty > 1 || lines.length > 1)) action = 'normalize';

    return {
      action: action,
      qualifies: qualifies,
      eligibleTotal: total,
      threshold: CFG.thresholdCents,
      giftQty: qty,
      giftLineCount: lines.length,
      lines: lines
    };
  }

  /* ---------- 是正 ---------- */

  /* 是正リクエストに sections を載せると、更新後のカートHTMLが同じ応答で返る。
     別途セクションを取得する往復を省ける */
  function withSections(payload) {
    payload.sections = CFG.sectionId || 'main';
    payload.sections_url = '/cart';
    return payload;
  }

  function addGift() {
    var payload = { items: [{ id: Number(CFG.giftVariantId), quantity: 1 }] };
    if (CFG.giftProperty) {
      payload.items[0].properties = {};
      /* 特典として追加した明細を注文データ上で識別するために付与する */
      payload.items[0].properties[CFG.giftProperty] = CFG.giftPropertyValue;
    }
    return postJson('/cart/add.js', withSections(payload));
  }

  /* 行番号ではなく line item key を使う。
     同一バリアントが複数行ある場合に行番号指定は誤爆するため。 */
  function setLineQuantity(key, quantity) {
    return postJson('/cart/change.js', withSections({ id: key, quantity: quantity }));
  }

  function removeAllGifts(lines) {
    return lines.reduce(function (chain, item) {
      return chain.then(function () {
        return setLineQuantity(item.key, 0);
      });
    }, Promise.resolve(null));
  }

  function normalizeGifts(lines) {
    /* 先頭の1行だけ残して数量1にし、残りを削除する */
    var keep = lines[0];
    var rest = lines.slice(1);
    return removeAllGifts(rest).then(function (last) {
      return keep.quantity === 1 ? last : setLineQuantity(keep.key, 1);
    });
  }

  function applyDecision(d) {
    if (d.action === 'add') return addGift();
    if (d.action === 'remove') return removeAllGifts(d.lines);
    if (d.action === 'normalize') return normalizeGifts(d.lines);
    return Promise.resolve();
  }

  /* 是正によるカート点数の増減 */
  function countDeltaOf(d) {
    if (d.action === 'add') return 1;
    if (d.action === 'remove') return -d.giftQty;
    if (d.action === 'normalize') return -(d.giftQty - 1);
    return 0;
  }

  /* ---------- 描画 ----------
     ページ全体を再読み込みせず、カート部分だけを差し替える。
     テーマ本体の refreshCart() と同じ対象・同じ方法のため、
     テーマ側のイベント委譲は維持される。 */

  var SWAP_TARGETS = [
    { selector: '[js-cart-items]', mode: 'inner' },
    { selector: '[js-cart-footer-subtotal-wrapper]', mode: 'outer' }
  ];

  var suppressObserver = false;

  /* ヘッダーのカート個数を増減する。カートページ以外で是正したときに使う */
  function adjustCartCount(delta) {
    if (!delta) return;
    var nodes = document.querySelectorAll('[data-js-cart-count]');
    for (var i = 0; i < nodes.length; i++) {
      var n = parseInt(nodes[i].textContent, 10);
      if (!isNaN(n)) nodes[i].textContent = String(Math.max(0, n + delta));
    }
  }

  function hasCartDom() {
    return !!document.querySelector('[js-cart-items]');
  }

  function renderCart(mutationResponse, countDelta) {
    /* カートページ以外には差し替える対象が無い。
       ページを再読み込みしては絶対にいけないので、個数表示だけ直して終わる */
    if (!hasCartDom()) {
      adjustCartCount(countDelta);
      log('カートページ以外のため、表示更新はカート個数のみ');
      return Promise.resolve();
    }

    var sectionId = CFG.sectionId || 'main';
    var inline =
      mutationResponse && mutationResponse.sections && mutationResponse.sections[sectionId];

    var source$ = inline
      ? Promise.resolve(inline)
      : fetch('/cart?section_id=' + sectionId, { headers: { Accept: 'text/html' } }).then(
          function (res) {
            return res.text();
          }
        );

    return source$
      .then(function (html) {
        var source = new DOMParser().parseFromString(html, 'text/html');
        var swapped = 0;

        /* 差し替えによる DOM 変化で自分自身が再判定されないよう抑止する */
        suppressObserver = true;

        SWAP_TARGETS.forEach(function (t) {
          var target = document.querySelector(t.selector);
          var src = source.querySelector(t.selector);
          if (!target || !src) return;
          if (t.mode === 'inner') {
            target.innerHTML = src.innerHTML;
          } else {
            target.outerHTML = src.outerHTML;
          }
          swapped++;
        });

        window.setTimeout(function () {
          suppressObserver = false;
        }, 50);

        if (swapped === 0) {
          warn('カート部分を特定できなかったため、ページを再読み込みします');
          window.location.reload();
          return;
        }

        log(
          'カート表示を更新しました（ページ再読み込みなし / ' +
            (inline ? '是正の応答を再利用' : 'セクションを追加取得') +
            '）'
        );
      })
      .catch(function (err) {
        warn('カート表示の更新に失敗したため、ページを再読み込みします', err);
        window.location.reload();
      });
  }

  /* ---------- 実行 ---------- */

  function report(d, cart, trigger) {
    log(
      '判定額 ' +
        yen(d.eligibleTotal) +
        ' / 閾値 ' +
        yen(d.threshold) +
        ' → 条件達成: ' +
        d.qualifies +
        ' / 特典 ' +
        d.giftQty +
        '点（明細' +
        d.giftLineCount +
        '行） → 是正: ' +
        d.action +
        '  [mode=' +
        CFG.mode +
        ' / 契機=' +
        trigger +
        ']'
    );

    /* 明細ごとの計上／除外を表示。代引き手数料とギフトボックスの扱いを目視確認できる */
    log(
      '明細の内訳:\n' +
        cart.items
          .map(function (item) {
            var mark = isGift(item) ? '除外(特典)' : isExcluded(item) ? '除外' : '計上';
            return (
              '  [' +
              mark +
              '] ' +
              yen(item.final_line_price) +
              ' ×' +
              item.quantity +
              '  ' +
              (item.sku || '(SKUなし)') +
              '  ' +
              item.product_title
            );
          })
          .join('\n')
    );
  }

  function ms(from) {
    return Math.round(performance.now() - from) + 'ms';
  }

  function run(trigger, presetCart) {
    if (running) return;
    running = true;

    var t0 = performance.now();
    var tFetch = 0;
    var tApply = 0;

    /* presetCart はサーバー側で埋め込んだ初期状態。通信せずに判定できる */
    var source = presetCart ? Promise.resolve(presetCart) : (setBusy(true), getCart());

    source
      .then(function (cart) {
        tFetch = performance.now();
        var d = decide(cart);
        report(d, cart, trigger);
        syncGiftOnlyLock(cart);

        if (d.action === 'none') {
          /* 整合が取れている＝安定した状態なので、連鎖防止カウンタを戻す */
          resetCorrectionCount();
          log(
            '所要時間: 合計 ' +
              ms(t0) +
              '（カート取得 ' +
              (presetCart ? '0ms／埋め込み利用' : Math.round(tFetch - t0) + 'ms') +
              '・是正なし）'
          );
          return;
        }

        if (DRY_RUN) {
          log('mode=検証モード のため是正は行いません');
          return;
        }

        if (shouldSkipCorrection(d.action)) {
          warn(
            '特典の付与を見送りました（追加と削除の往復を検知。他アプリと判定が競合している可能性があります）',
            d
          );
          return;
        }

        /* ここで初めて封鎖する。是正不要なカートではボタンがちらつかない */
        setBusy(true);
        noteCorrection(d.action);
        log('是正を実行します:', d.action);

        return applyDecision(d)
          .then(function (res) {
            tApply = performance.now();
            /* 是正後のカートで再評価。特典を外した結果ロックが外れる */
            syncGiftOnlyLock(res);
            return renderCart(res, countDeltaOf(d));
          })
          .then(function () {
            log(
              '所要時間: 合計 ' +
                ms(t0) +
                '（カート取得 ' +
                (presetCart ? '0ms／埋め込み利用' : Math.round(tFetch - t0) + 'ms') +
                '・是正 ' +
                Math.round(tApply - tFetch) +
                'ms・描画 ' +
                Math.round(performance.now() - tApply) +
                'ms）'
            );
          });
      })
      .catch(function (err) {
        warn('処理中にエラーが発生しました', err);
      })
      .then(function () {
        running = false;
        setBusy(false);
      });
  }

  /* カートページ内の削除・数量変更はページ遷移を伴わないため、
     DOMの変化を監視して再判定する。連続した変化はまとめて1回に間引く */
  function watchCartChanges() {
    var target = document.querySelector('.js-cart-form-items');
    if (!target || typeof MutationObserver === 'undefined') return;

    var timer = null;
    var observer = new MutationObserver(function () {
      if (suppressObserver) return;
      /* 変更を検知した時点で即座にレジへの導線を止める */
      setBusy(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        run('カート変更');
      }, MUTATION_DEBOUNCE_MS);
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  /* テーマがカートを操作したときに document へ発行する DOM イベントを購読する。
     商品ページ等で購入額が閾値に達した瞬間に特典を追加できるため、
     カートページには最初から特典が入った状態で到達できる。
     （カートページ上の変更は watchCartChanges が担当するので二重に動かさない） */
  function listenCartEvents() {
    var timer = null;
    var events = ['cart:updated', 'cart:item-added'];

    function onCartEvent(e) {
      log('テーマのカートイベントを検知:', e.type);
      if (hasCartDom()) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        run('カート更新イベント');
      }, 150);
    }

    for (var i = 0; i < events.length; i++) {
      document.addEventListener(events[i], onCartEvent);
    }
  }

  function onDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* DOM が必要な初期化。チェックアウト封鎖とカート変更の監視 */
  function initInteractive() {
    blockSubmitWhileBusy();
    watchCartChanges();
  }

  /* ---------- 起動 ----------
     このスクリプトは <head> で読み込まれる。埋め込み済みの初期状態から
     DOM を待たずに判定し、是正が必要ならリクエストを即座に発行する。
     これによりネットワーク往復がページの描画と並行して進む。 */
  function boot() {
    listenCartEvents();

    var initial = CFG.initialCart;

    /* 初期状態が無い、または他アプリの操作を待つ設定のときは
       従来どおり DOM 準備後に /cart.js を取得して判定する */
    if (!initial || !initial.items || CFG.settleDelayMs > 0) {
      onDomReady(function () {
        initInteractive();
        if (CFG.settleDelayMs > 0) {
          setBusy(true);
          window.setTimeout(function () {
            run('ページ読み込み');
          }, CFG.settleDelayMs);
        } else {
          run('ページ読み込み');
        }
      });
      return;
    }

    var d = decide(initial);
    report(d, initial, 'ページ読み込み');
    onDomReady(function () {
      syncGiftOnlyLock(initial);
    });

    if (d.action === 'none') {
      resetCorrectionCount();
      onDomReady(initInteractive);
      return;
    }

    if (DRY_RUN) {
      log('mode=検証モード のため是正は行いません');
      onDomReady(initInteractive);
      return;
    }

    if (shouldSkipCorrection(d.action)) {
      warn('特典の付与を見送りました（追加と削除の往復を検知）', d);
      onDomReady(initInteractive);
      return;
    }

    running = true;
    noteCorrection(d.action);
    var t0 = performance.now();
    log('是正を実行します:', d.action, '（描画を待たずに送信）');

    /* ここが本命。DOM の完成を待たずに送信する */
    var request = applyDecision(d);
    var tApply = 0;

    onDomReady(function () {
      initInteractive();
      if (running) setBusy(true);
    });

    request
      .then(function (res) {
        tApply = performance.now();
        return new Promise(function (resolve) {
          onDomReady(resolve);
        }).then(function () {
          /* 是正後のカートで再評価。特典を外した結果ロックが外れる */
          syncGiftOnlyLock(res);
          return renderCart(res, countDeltaOf(d));
        });
      })
      .then(function () {
        log(
          '所要時間: 是正 ' +
            Math.round(tApply - t0) +
            'ms・描画 ' +
            Math.round(performance.now() - tApply) +
            'ms（合計 ' +
            ms(t0) +
            '）'
        );
      })
      .catch(function (err) {
        warn('処理中にエラーが発生しました', err);
      })
      .then(function () {
        running = false;
        setBusy(false);
      });
  }

  boot();
})();
